import { useState, useEffect } from 'react';
import { Modal, Backdrop, Box, Typography } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';

import Button from 'src/components/NewButton';
import Checkbox from 'src/components/Checkbox';

const useStyles = makeStyles(() =>
  createStyles({
    modal: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    paper: {
      backgroundColor: '#fff',
      borderRadius: 8,
      boxShadow:
        '0px 8px 8px -4px rgba(16, 24, 40, 0.03), 0px 20px 24px -4px rgba(16, 24, 40, 0.08)',
      padding: 24,
      width: 500,
      maxHeight: 600,
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      marginBottom: 16,
    },
    searchContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '8px 12px',
      border: '1px solid #D0D5DD',
      borderRadius: 8,
      marginBottom: 12,
    },
    searchInput: {
      border: 'none',
      outline: 'none',
      flex: 1,
      fontSize: 14,
    },
    options: {
      flex: 1,
      overflowY: 'auto',
      maxHeight: 400,
    },
    option: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '4px 0',
    },
    children: {
      marginLeft: 24,
    },
    actions: {
      display: 'flex',
      gap: 8,
      marginTop: 16,
      justifyContent: 'flex-end',
    },
  })
);

interface IABSelectorProps {
  open: boolean;
  onClose: () => void;
  iabCategories: any[];
  selectedIABCategories: string[];
  onSelect: (categories: string[]) => void;
}

const formatTreeData = (tree: any[], selected: string[]): any[] => {
  return tree.map((item) => {
    const hasChildren = !!item.children?.length;
    const children = hasChildren ? formatTreeData(item.children, selected) : [];
    const isChecked =
      selected.includes(item.name) || children.some((c) => c.isChecked);

    return {
      ...item,
      isChecked,
      isExpanded: false,
      children,
    };
  });
};

const filterTreeData = (
  tree: any[],
  key: string,
  isParentShow = false
): any[] => {
  return tree.map((item) => {
    const isShow = item.name.toLowerCase().includes(key.toLowerCase());
    const children =
      item.children && item.children.length
        ? filterTreeData(item.children, key, isShow || isParentShow)
        : [];

    return {
      ...item,
      isShow:
        key === '' || isShow || isParentShow || children.some((c) => c.isShow),
      children,
    };
  });
};

const updateTreeData = (
  tree: any[],
  id: string,
  property: string,
  value: any
): any[] => {
  return tree.map((node) => {
    if (node.id === id) {
      const updated = { ...node, [property]: value };
      if (property === 'isChecked' && node.children?.length) {
        updated.children = node.children.map((child: any) => ({
          ...child,
          isChecked: value,
        }));
      }
      return updated;
    }
    if (node.children?.length) {
      const children = updateTreeData(node.children, id, property, value);
      const isChecked =
        property === 'isChecked'
          ? children.every((child) => child.isChecked)
          : node.isChecked;
      return { ...node, children, isChecked };
    }
    return node;
  });
};

const saveSelection = (tree: any[]): string[] => {
  let result: string[] = [];
  tree.forEach((item) => {
    if (item.isChecked && !item.children?.length) {
      result.push(item.name);
    }
    if (item.children?.length) {
      result = [...result, ...saveSelection(item.children)];
    }
  });
  return result;
};

export const IABSelector = ({
  open,
  onClose,
  iabCategories,
  selectedIABCategories,
  onSelect,
}: IABSelectorProps) => {
  const classes = useStyles();
  const [searchKey, setSearchKey] = useState('');
  const [data, setData] = useState<any[]>([]);

  useEffect(() => {
    if (open) {
      setSearchKey('');
      const formatted = formatTreeData(iabCategories, selectedIABCategories);
      setData(formatted);
    }
  }, [open, iabCategories, selectedIABCategories]);

  useEffect(() => {
    if (open) {
      const filtered = filterTreeData(data, searchKey);
      setData(filtered);
    }
  }, [searchKey]);

  const showCategories = (categories: any[]) => {
    return categories
      .filter((item) => item.isShow)
      .map((item) => {
        const hasChildren = !!item.children?.length;

        return (
          <Box key={item.id}>
            <Box className={classes.option}>
              {hasChildren ? (
                item.isExpanded ? (
                  <KeyboardArrowDownIcon
                    onClick={() => {
                      setData(
                        updateTreeData(data, item.id, 'isExpanded', false)
                      );
                    }}
                    style={{ cursor: 'pointer', fontSize: 16 }}
                  />
                ) : (
                  <KeyboardArrowRightIcon
                    onClick={() => {
                      setData(
                        updateTreeData(data, item.id, 'isExpanded', true)
                      );
                    }}
                    style={{ cursor: 'pointer', fontSize: 16 }}
                  />
                )
              ) : (
                <Box style={{ width: 16 }} />
              )}
              <Checkbox
                name={item.id}
                label=''
                checked={item.isChecked}
                onChange={() => {
                  setData(
                    updateTreeData(data, item.id, 'isChecked', !item.isChecked)
                  );
                }}
              />
              <Typography style={{ fontSize: 14 }}>{item.name}</Typography>
            </Box>
            {hasChildren && item.isExpanded && (
              <Box className={classes.children}>
                {showCategories(item.children)}
              </Box>
            )}
          </Box>
        );
      });
  };

  const handleSave = () => {
    const selected = saveSelection(data);
    onSelect(selected);
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={classes.modal}
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Box className={classes.paper}>
        <Box className={classes.header}>
          <Typography variant='h6' style={{ fontSize: 18, fontWeight: 600 }}>
            Select IAB Categories
          </Typography>
        </Box>
        <Box className={classes.searchContainer}>
          <SearchIcon style={{ color: '#667085' }} />
          <input
            type='text'
            placeholder='Search IAB categories...'
            value={searchKey}
            onChange={(e) => setSearchKey(e.target.value)}
            className={classes.searchInput}
          />
        </Box>
        <Box className={classes.options}>{showCategories(data)}</Box>
        <Box className={classes.actions}>
          <Button
            title='Cancel'
            variant='outlined'
            height={36}
            onClick={onClose}
          />
          <Button title='Select' height={36} onClick={handleSave} />
        </Box>
      </Box>
    </Modal>
  );
};
